import { isIosCompanionApp } from "@/lib/native/runtime";
import { useState } from "react";
import { Button } from "@a2z/ui";
import type { SellerAuthSessionResponse } from "@/lib/api/types";
import { useTranslations } from "@/lib/i18n/intl";
import { keys, getLocaleDirection, type PortalLocale } from "@/lib/i18n/portal";
import { LogoutButton } from "@/shared/layout/LogoutButton";
import { PasswordModal } from "@/shared/profile/components/PasswordModal";
import { useStoreSwitch } from "@/shared/services/useStoreSwitch";
import { AccessCard, PauseIcon, useDocumentTitle } from "./AccessCard";

/**
 * A team member whose store no longer admits them (a Free store is the owner's
 * alone). Rendered without the shell — there is nothing in this store they can
 * open — but in the same card pattern as the in-shell denied screen.
 *
 * Every way forward that still works is here: switch to another store that
 * admits them, change their password (in place — the account pages sit behind
 * this same gate, and the first-login page only serves a forced change), or
 * sign out.
 */
export function PausedAccess({
  session,
  locale,
  storeSlug,
}: {
  session: SellerAuthSessionResponse;
  locale: PortalLocale;
  storeSlug: string;
}) {
  const t = useTranslations();
  const { switchTo, isSwitching, error } = useStoreSwitch(locale, storeSlug);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const title = t(keys.seller.accessPausedTitle);
  useDocumentTitle(`${title} - Trioty`);
  const otherStores = (session.availableStores ?? []).filter(
    (store) => store.storeSlug !== storeSlug && store.storeAccess === true,
  );

  return (
    <main
      dir={getLocaleDirection(locale)}
      lang={locale}
      className="flex min-h-[100svh] items-center bg-[color:var(--canvas)] px-4 py-8"
    >
      <AccessCard
        icon={<PauseIcon />}
        title={title}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              size="lg"
              onClick={() => setPasswordOpen(true)}
            >
              {t(keys.seller.accessPassword)}
            </Button>
            <LogoutButton
              redirectTo={`/${locale}/login`}
              className="h-10 px-[18px] text-sm"
            />
          </>
        }
      >
        <p className="m-0">{t(isIosCompanionApp() ? keys.iosCompanion.paused : keys.seller.accessPausedBody)}</p>
        {otherStores.length > 0 ? (
          <div className="mt-3 flex flex-col gap-2">
            <p className="m-0 text-[13px] font-semibold text-[color:var(--text-muted)]">
              {t(keys.seller.accessSwitchTitle)}
            </p>
            {otherStores.map((store) => (
              <Button
                key={store.storeId}
                type="button"
                // One store to go to is THE way forward; several are a choice.
                variant={otherStores.length === 1 ? "primary" : "outline"}
                size="lg"
                fullWidth
                disabled={isSwitching}
                onClick={() => void switchTo(store.storeSlug)}
              >
                {store.storeName}
              </Button>
            ))}
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="m-0 text-[13px] text-[color:var(--danger-500,var(--error-500))]">
            {error}
          </p>
        ) : null}
      </AccessCard>
      <PasswordModal
        isOpen={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        storeSlug={storeSlug}
      />
    </main>
  );
}
